import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { onValue, ref as dbRef } from 'firebase/database'
import {
  Map as MapIcon,
  ClipboardList,
  Tv,
  Camera,
  DollarSign,
  Trash2,
  ArrowLeft,
  ExternalLink,
  Home as HomeIcon,
  Timer,
  Crosshair,
} from 'lucide-react'

import { db } from '../lib/firebase.js'
import {
  seedTestTournament,
  seedScorecardTest,
  seedPaceOfPlayTest,
  deleteTestTournament,
  deleteAllTestTournaments,
  deleteTestPhotos,
  ensureScorecardTestTournament,
  TEST_PHOTO_TOURNAMENT_ID,
  SCORECARD_TEST_TOURNAMENT_ID,
} from '../lib/teststation.js'
import CLUB from '../config/club.js'

const PANEL_BG = '#0F2E1E'
const PANEL_BORDER = 'rgba(var(--ge-primary-rgb),0.30)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'

export default function TestStationApp() {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const [testCount, setTestCount] = useState(0)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkNote, setBulkNote] = useState('')

  useEffect(() => {
    const off = onValue(dbRef(db, 'tournaments'), (snap) => {
      const v = snap.val() || {}
      let n = 0
      for (const [id, t] of Object.entries(v)) {
        // The persistent scorecard fixture isn't a throwaway — don't count it.
        if (id === SCORECARD_TEST_TOURNAMENT_ID) continue
        if (t?.info?.isTestStation) n++
      }
      setTestCount(n)
    })
    return () => off()
  }, [])

  const cleanAll = async () => {
    setBulkBusy(true)
    setBulkNote('')
    try {
      const removed = await deleteAllTestTournaments()
      setBulkNote(`Removed ${removed} test tournament${removed === 1 ? '' : 's'}.`)
      setTimeout(() => setBulkNote(''), 3000)
    } catch (e) {
      setBulkNote(e?.message || 'Cleanup failed')
    } finally {
      setBulkBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ge-primary)', color: '#fff', padding: '24px 16px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            {/* Back + Home — hard rule across every Test Station surface so
                a tester can always escape regardless of where they landed. */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Link to="/admin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)', fontSize: 12, textDecoration: 'none' }}>
                <ArrowLeft size={14} /> Back to Admin
              </Link>
              <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.7)', fontSize: 12, textDecoration: 'none' }}>
                <HomeIcon size={14} /> Home
              </Link>
            </div>
            <div style={{ marginTop: 6, fontSize: 10, letterSpacing: 2, color: ACCENT, fontWeight: 800 }}>
              🧪 TOOLS
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 900, margin: '4px 0 6px' }}>Test Station</h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 720, margin: 0 }}>
              Spin up sandbox tournaments to verify each feature without affecting
              real events. Every tile creates its own throwaway tournament tagged
              <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 4, marginLeft: 4, fontSize: 11 }}>
                isTestStation
              </code>{' '}— hidden from the admin dashboard lists.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{
              padding: '6px 12px', borderRadius: 999,
              border: `1px solid ${ACCENT}66`, background: 'rgba(0,0,0,0.35)',
              fontSize: 12, fontWeight: 800, letterSpacing: 1,
            }}>
              {testCount} active test tournament{testCount === 1 ? '' : 's'}
            </div>
            <button
              type="button"
              onClick={cleanAll}
              disabled={bulkBusy || testCount === 0}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                borderRadius: 10, border: '1px solid rgba(239,68,68,0.55)',
                background: testCount === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(239,68,68,0.18)',
                color: testCount === 0 ? 'rgba(255,255,255,0.4)' : '#FCA5A5',
                fontWeight: 800, fontSize: 12, letterSpacing: 0.5,
                cursor: testCount === 0 ? 'not-allowed' : 'pointer',
              }}>
              <Trash2 size={14} /> Clean Up All Test Data
            </button>
            {bulkNote ? (
              <div style={{ fontSize: 11, color: '#86EFAC' }}>{bulkNote}</div>
            ) : null}
          </div>
        </header>

        <div
          style={{
            display: 'grid', gap: 16, marginTop: 22,
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          }}>
          {/* GPS Map Test / calibration-sandbox tiles retired — the ONE map is
              /tv/scrollmap, and spot placement lives on its "Place spots" tool. */}
          <ScorecardUITile origin={origin} />
          <ScorecardTile origin={origin} />
          <PaceTile origin={origin} />
          <TvTile origin={origin} />
          <PhotoTile origin={origin} />
          <CalcuttaTile origin={origin} />
        </div>
      </div>
    </div>
  )
}

// ── Tile chrome ────────────────────────────────────────────────────────

function Tile({ Icon, title, blurb, accentColor, children }) {
  const color = accentColor || ACCENT
  return (
    <section
      style={{
        background: PANEL_BG,
        border: `1px solid ${PANEL_BORDER}`,
        borderRadius: 16,
        padding: 18,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 40, height: 40, borderRadius: 10,
            background: `${color}22`, border: `1px solid ${color}55`,
            color, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
          <Icon size={20} strokeWidth={2} />
        </div>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 900, margin: 0 }}>{title}</h2>
          <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', margin: '2px 0 0', lineHeight: 1.4 }}>
            {blurb}
          </p>
        </div>
      </div>
      {children}
    </section>
  )
}

function PrimaryBtn({ onClick, href, target, disabled, children, color }) {
  const c = color || ACCENT
  const style = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '9px 14px', borderRadius: 10,
    background: disabled ? 'rgba(255,255,255,0.08)' : c,
    color: disabled ? 'rgba(255,255,255,0.4)' : 'var(--ge-primary)',
    border: 'none', fontWeight: 800, fontSize: 12, letterSpacing: 0.4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    textDecoration: 'none',
  }
  if (href) {
    return <a href={href} target={target || '_blank'} rel="noreferrer" style={style}>{children}</a>
  }
  return <button type="button" onClick={onClick} disabled={disabled} style={style}>{children}</button>
}

function GhostBtn({ onClick, disabled, children, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '9px 14px', borderRadius: 10,
        border: danger ? '1px solid rgba(239,68,68,0.55)' : '1px solid rgba(255,255,255,0.22)',
        background: danger ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.04)',
        color: danger ? '#FCA5A5' : '#fff',
        fontWeight: 700, fontSize: 12, letterSpacing: 0.4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}>
      {children}
    </button>
  )
}

function StatusLine({ ok, busy, error }) {
  if (busy) return <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Working…</div>
  if (error) return <div style={{ fontSize: 11, color: '#FCA5A5' }}>{error}</div>
  if (ok) return <div style={{ fontSize: 11, color: '#86EFAC' }}>{ok}</div>
  return null
}

// ── Tile 1b: GPS Home Calibration ─────────────────────────────────────
// FULLY-ISOLATED home environment (its own RTDB node, 8-yard circles). Same
// scrolling map + placement + geofence system as Real Course. Scan the QR on
// the iPhone to broadcast GPS; Open Course on the iPad to place tee/fairway/
// green dots and walk the zones. Cannot touch real-course data.
function GpsCalibrationTile({ origin }) {
  const courseUrl = `${origin}/teststation/home`
  const broadcastUrl = `${origin}/teststation/home/broadcast`
  return (
    <Tile
      Icon={Crosshair}
      title="GPS Home Calibration"
      blurb="Home-testing environment — 8-yard geofence circles, its OWN isolated data store (never touches the real course). Scan the QR on the iPhone to broadcast GPS, then Open Course on the iPad to place tee / fairway / green dots and walk the zones."
      accentColor="#FBBF24">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(56,189,248,0.06)', padding: 12, borderRadius: 10 }}>
        <QRCodeCanvas value={broadcastUrl} size={96} includeMargin />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            📡 SCAN ON IPHONE
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, wordBreak: 'break-all' }}>
            {broadcastUrl.replace(/^https?:\/\//, '')}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
            8-yard circles · own data store · broadcasts continuously
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={courseUrl} color="#FBBF24"><ExternalLink size={13} /> Open Course (iPad)</PrimaryBtn>
      </div>
    </Tile>
  )
}

// ── Tile 1c: Real Course Calibration ──────────────────────────────────
// On-course environment (its own RTDB node, 50-yard circles). Same system as
// GPS Home Calibration. Scan the QR on the iPhone to broadcast live GPS; open
// the course on the iPad to place tee/fairway/green dots and walk the zones.
function GpsCalibrationMap2Tile({ origin }) {
  const courseUrl = `${origin}/teststation/map2`
  const broadcastUrl = `${origin}/teststation/map2/broadcast`
  return (
    <Tile
      Icon={Crosshair}
      title="Real Course Calibration"
      blurb="On-course environment — 50-yard geofence circles, its OWN isolated data store. Scan the QR on the iPhone to broadcast GPS, then Open Course on the iPad to place tee / fairway / green dots and walk the zones."
      accentColor="#FBBF24">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(56,189,248,0.06)', padding: 12, borderRadius: 10 }}>
        <QRCodeCanvas value={broadcastUrl} size={96} includeMargin />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            📡 SCAN ON IPHONE
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, wordBreak: 'break-all' }}>
            {broadcastUrl.replace(/^https?:\/\//, '')}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
            Turns GPS on · keeps the screen awake · broadcasts continuously
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={courseUrl} color="#FBBF24"><ExternalLink size={13} /> Open Course (iPad)</PrimaryBtn>
      </div>
    </Tile>
  )
}

// ── Tile 1: GPS Map Test ──────────────────────────────────────────────
function GpsTile({ origin }) {
  const broadcasterUrl = `${origin}/gpstest`
  const pbccWatcherUrl = `${origin}/gpstestwatch`
  const liveWatcherUrl = `${origin}/gpslivetest`
  return (
    <Tile
      Icon={MapIcon}
      title="GPS Map Test"
      blurb="Broadcast a phone's GPS to either the PBCC course map or a live Google Maps view. Auto-clears after 10 min of inactivity — no test data to delete."
      accentColor="#22D3EE">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 10 }}>
        <QRCodeCanvas value={broadcasterUrl} size={88} includeMargin />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            BROADCASTER URL
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, wordBreak: 'break-all' }}>
            {broadcasterUrl.replace(/^https?:\/\//, '')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={pbccWatcherUrl}><ExternalLink size={13} /> PBCC Course Map</PrimaryBtn>
        <PrimaryBtn href={liveWatcherUrl} color="#3B82F6"><ExternalLink size={13} /> Live Location (Google Maps)</PrimaryBtn>
      </div>
    </Tile>
  )
}

// ── Tile 1c: Scorecard UI Test (persistent) ───────────────────────────
// One fixed tournament + dummy "Ratliff/Keese" team with a QR that never changes.
// Scan it to land straight on the live scorecard and eyeball UI changes —
// no need to seed a throwaway tournament each time. Self-heals on mount via
// ensureScorecardTestTournament so the QR is always valid.
function ScorecardUITile({ origin }) {
  const [ready, setReady] = useState(null) // { tournamentId, flightId, teamId }
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    ensureScorecardTestTournament()
      .then((r) => { if (alive) setReady(r) })
      .catch((e) => { if (alive) setErr(e?.message || 'Setup failed') })
    return () => { alive = false }
  }, [])

  const url = ready
    ? `${origin}/score?t=${ready.tournamentId}&f=${ready.flightId}&team=${ready.teamId}`
    : ''

  return (
    <Tile
      Icon={ClipboardList}
      title="Scorecard UI Test"
      blurb="Permanent QR to a fixed test scorecard (team “Ratliff/Keese”). Scan it any time to see live scorecard UI changes — no new tournament needed. Survives Clean Up All Test Data."
      accentColor="#A855F7">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 10 }}>
        {url ? (
          <div style={{ background: '#fff', borderRadius: 8, padding: 6, flexShrink: 0 }}>
            <QRCodeCanvas value={url} size={96} includeMargin />
          </div>
        ) : (
          <div style={{ width: 108, height: 108, borderRadius: 8, background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
            {err ? '⚠️' : 'Setting up…'}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            SCAN ON IPHONE
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, wordBreak: 'break-all' }}>
            {url ? url.replace(/^https?:\/\//, '') : '—'}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
            Fixed tournament · team “Ratliff/Keese”
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={url || undefined} disabled={!url} color="#A855F7">
          <ExternalLink size={13} /> Open Scorecard
        </PrimaryBtn>
      </div>
      <StatusLine busy={!ready && !err} error={err} ok={ready ? 'Ready — scan to open the live scorecard.' : ''} />
    </Tile>
  )
}

// ── Tile 2: Scorecard Test ────────────────────────────────────────────
function ScorecardTile({ origin }) {
  const [active, setActive] = useState(null) // { tournamentId, flightId, teams, teamCount }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const seed = async (teamCount) => {
    setBusy(true); setErr('')
    try {
      const result = await seedScorecardTest(teamCount)
      setActive(result)
    } catch (e) {
      setErr(e?.message || 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  const teardown = async () => {
    if (!active) return
    setBusy(true); setErr('')
    try {
      await deleteTestTournament(active.tournamentId)
      setActive(null)
    } catch (e) {
      setErr(e?.message || 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Tile
      Icon={ClipboardList}
      title="Scorecard Test"
      blurb="Generate test groups with QR codes that open real scorecards on iPhone. Scores stream live to /testleaderboard; GPS dots and photos taken from inside the scorecard ride the same test session — open all watcher screens in one go below.">
      {!active ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <PrimaryBtn onClick={() => seed(1)} disabled={busy}>Single Team</PrimaryBtn>
          <PrimaryBtn onClick={() => seed(2)} disabled={busy} color="#A855F7">2 Teams</PrimaryBtn>
          <PrimaryBtn onClick={() => seed(3)} disabled={busy} color="#A855F7">3 Teams</PrimaryBtn>
          <PrimaryBtn onClick={() => seed(4)} disabled={busy} color="#A855F7">4 Teams</PrimaryBtn>
        </div>
      ) : (
        <>
          <div style={{
            background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 10,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10,
            maxHeight: 320, overflowY: 'auto',
          }}>
            {Object.entries(active.teams).map(([teamId, t]) => {
              const url = `${origin}/score?t=${active.tournamentId}&f=${active.flightId}&team=${teamId}`
              return (
                <div key={teamId} style={{ background: '#fff', borderRadius: 8, padding: 8, color: '#111', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800 }}>{t.info?.name || teamId}</div>
                  <div style={{ fontSize: 9, color: '#6B7280', marginTop: 1 }}>
                    {(t.info?.players || []).join(' · ')}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                    <QRCodeCanvas value={url} size={96} includeMargin />
                  </div>
                  <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: PRIMARY, textDecoration: 'underline', wordBreak: 'break-all' }}>
                    Open scorecard
                  </a>
                </div>
              )
            })}
          </div>

          {/* Single launchpad for every watcher screen in one place. Open
              each on a separate TV/iPad/laptop and the full test runs in
              parallel. Colors match the source tile so it's visually
              obvious which feed each button is targeting. */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px dashed rgba(255,255,255,0.12)',
            borderRadius: 10, padding: 10,
          }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
              OPEN ON BIG SCREENS
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <PrimaryBtn href={`${origin}/testleaderboard`} color="#F59E0B"><ExternalLink size={13} /> Watch Leaderboard</PrimaryBtn>
              <PrimaryBtn href={`${origin}/gpstestwatch`} color="#22D3EE"><ExternalLink size={13} /> Watch GPS Map</PrimaryBtn>
              <PrimaryBtn href={`${origin}/testphotowall`} color="#EC4899"><ExternalLink size={13} /> Watch Photo Wall</PrimaryBtn>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PrimaryBtn href={`${origin}/admin/t/${active.tournamentId}`}><ExternalLink size={13} /> Open in Admin</PrimaryBtn>
            <GhostBtn onClick={teardown} disabled={busy} danger><Trash2 size={13} /> Clean Up</GhostBtn>
          </div>
        </>
      )}
      <StatusLine busy={busy && !active} error={err} ok={active ? `${active.teamCount} team${active.teamCount === 1 ? '' : 's'} ready · scores → /testleaderboard · GPS → /gpstestwatch · photos → /testphotowall.` : ''} />
    </Tile>
  )
}

// ── Tile 2b: Pace of Play Test ────────────────────────────────────────
// Seeds a 2-team tournament with each team on a different starting hole
// (1 + 2) → two independent pace groups. Real 15-min timer, real Firebase
// writes. To see the red alert without waiting, only one team should
// score holes — the other team's timer ticks down to 0 and goes red on
// its own scorecard, and its GPS-marker ring on the Command Center turns
// red with the source pulse.
function PaceTile({ origin }) {
  const [active, setActive] = useState(null) // { tournamentId, flightId, teams }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // Live pace snapshot for the active test tournament so the tile shows
  // the timer ticking down without the user opening the Command Center.
  const [pace, setPace] = useState({})
  useEffect(() => {
    if (!active?.tournamentId) { setPace({}); return }
    const off = onValue(dbRef(db, `tournaments/${active.tournamentId}/paceOfPlay`), (snap) => {
      setPace(snap.val() || {})
    })
    return () => off()
  }, [active?.tournamentId])

  // 1s tick so the elapsed/remaining readout below refreshes between
  // Firebase events (Firebase only fires when scores save).
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [active])

  const seed = async (teamCount) => {
    setBusy(true); setErr('')
    try {
      const result = await seedPaceOfPlayTest(teamCount)
      setActive(result)
    } catch (e) {
      setErr(e?.message || 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  const teardown = async () => {
    if (!active) return
    setBusy(true); setErr('')
    try {
      await deleteTestTournament(active.tournamentId)
      setActive(null)
    } catch (e) {
      setErr(e?.message || 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Tile
      Icon={Timer}
      title="⏱ Pace of Play Test"
      blurb="Compressed 5-min timer for testing. 1 player: solo run, watch yellow at 2 min remaining + red SOURCE at 0:00. 2 players: independent groups on holes 1 + 2 — score on one to keep it green, leave the other to fire red.">
      {!active ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <PrimaryBtn onClick={() => seed(1)} disabled={busy} color="#F87171">1 Player</PrimaryBtn>
          <PrimaryBtn onClick={() => seed(2)} disabled={busy} color="#F87171">2 Players</PrimaryBtn>
        </div>
      ) : (
        <>
          <div style={{
            background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 10,
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10,
          }}>
            {Object.entries(active.teams).map(([teamId, t]) => {
              const url = `${origin}/score?t=${active.tournamentId}&f=${active.flightId}&team=${teamId}`
              const sh = t.info?.startingHole
              const groupKey = `${active.flightId}__h${String(sh).padStart(2, '0')}`
              const g = pace[groupKey]
              // 5/3-min thresholds mirror the seeded info.paceLimitMs/
              // paceWarnMs so the chip status matches what the phone
              // scorecard renders for this test tournament.
              const PACE_TEST_LIMIT = 5 * 60 * 1000
              const PACE_TEST_WARN  = 3 * 60 * 1000
              const elapsed = g?.holeStartTime ? Math.max(0, Date.now() - g.holeStartTime) : 0
              const remaining = g?.holeStartTime ? PACE_TEST_LIMIT - elapsed : null
              const status =
                !g?.holeStartTime ? 'idle' :
                elapsed >= PACE_TEST_LIMIT ? 'red' :
                elapsed >= PACE_TEST_WARN  ? 'yellow' : 'green'
              const statusColor = { idle: '#9CA3AF', green: '#22C55E', yellow: '#FBBF24', red: '#DC2626' }[status]
              const fmt = (ms) => {
                if (ms == null) return '—'
                const abs = Math.max(0, Math.floor(Math.abs(ms) / 1000))
                const m = Math.floor(abs / 60)
                const s = abs % 60
                return `${m}:${String(s).padStart(2, '0')}${ms < 0 ? ' over' : ''}`
              }
              return (
                <div key={teamId} style={{ background: '#fff', borderRadius: 8, padding: 8, color: '#111', textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800 }}>{t.info?.name || teamId}</div>
                  <div style={{ fontSize: 9, color: '#6B7280', marginTop: 1 }}>
                    Starts on Hole {sh}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                    <QRCodeCanvas value={url} size={96} includeMargin />
                  </div>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: `${statusColor}22`, color: '#111',
                    border: `1px solid ${statusColor}`,
                    padding: '2px 6px', borderRadius: 4,
                    fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
                    {status === 'idle' ? 'Not started' : `${fmt(remaining)} left`}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: PRIMARY, textDecoration: 'underline', wordBreak: 'break-all' }}>
                      Open scorecard
                    </a>
                  </div>
                </div>
              )
            })}
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px dashed rgba(255,255,255,0.12)',
            borderRadius: 10, padding: 10,
          }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
              ADMIN WATCH SCREENS
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <PrimaryBtn href={`${origin}/map?t=${active.tournamentId}`} color="#F87171">
                <ExternalLink size={13} /> Command Center
              </PrimaryBtn>
              <PrimaryBtn href={`${origin}/testleaderboard`} color="#F59E0B">
                <ExternalLink size={13} /> Watch Leaderboard
              </PrimaryBtn>
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
            <strong style={{ color: '#FDE68A' }}>To see the red alert:</strong>{' '}
            start scoring at least one hole to launch the timer, then stop —
            the timer ticks down through yellow at 2 min remaining and fires
            the red SOURCE banner at 0:00 (5-min test budget).
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PrimaryBtn href={`${origin}/admin/t/${active.tournamentId}`}><ExternalLink size={13} /> Open in Admin</PrimaryBtn>
            <GhostBtn onClick={teardown} disabled={busy} danger><Trash2 size={13} /> Clean Up</GhostBtn>
          </div>
        </>
      )}
      <StatusLine busy={busy && !active} error={err} ok={active ? 'Pace test live — real 15-min timer running.' : ''} />
    </Tile>
  )
}

// ── Tile 3: TV Leaderboard Test ───────────────────────────────────────
// No seeding — this is a live display test. Multiple TVs/iPads open the
// same /testleaderboard URL; it auto-discovers the active Scorecard Test
// tournament and streams scores onto every screen at once.
function TvTile({ origin }) {
  const watcherUrl = `${origin}/testleaderboard`
  return (
    <Tile
      Icon={Tv}
      title="TV Leaderboard Test"
      blurb="Live display test — no setup. Open the URL on any TV, iPad, or PC. Multiple screens stay in sync, all pulling scores from the active Scorecard Test."
      accentColor="#F59E0B">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 10 }}>
        <QRCodeCanvas value={watcherUrl} size={88} includeMargin />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            WATCH LEADERBOARD URL
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, marginTop: 2, wordBreak: 'break-all' }}>
            {watcherUrl.replace(/^https?:\/\//, '')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={watcherUrl} color="#F59E0B"><ExternalLink size={13} /> Open Leaderboard</PrimaryBtn>
      </div>
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.45 }}>
        Start a <strong>Scorecard Test</strong> above, then scores entered on
        each iPhone update this screen in real time. Nothing to clean up here —
        deleting the Scorecard Test takes its leaderboard with it.
      </p>
    </Tile>
  )
}

// ── Tile 4: Photo Wall Test ───────────────────────────────────────────
// No tournament needed. Phones hit /testphoto (camera capture, auto-approved)
// and every /testphotowall tab streams live submissions.
function PhotoTile({ origin }) {
  const [photoCount, setPhotoCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [ok, setOk] = useState('')
  const uploadUrl = `${origin}/testphoto`
  const wallUrl   = `${origin}/testphotowall`

  useEffect(() => {
    const off = onValue(
      dbRef(db, `tournaments/${TEST_PHOTO_TOURNAMENT_ID}/photos`),
      (snap) => {
        const v = snap.val() || {}
        setPhotoCount(Object.keys(v).length)
      },
    )
    return () => off()
  }, [])

  const cleanup = async () => {
    setBusy(true); setErr(''); setOk('')
    try {
      await deleteTestPhotos()
      setOk('Test photos cleared.')
      setTimeout(() => setOk(''), 2500)
    } catch (e) {
      setErr(e?.message || 'Cleanup failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Tile
      Icon={Camera}
      title="Photo Wall Test"
      blurb="Phone scans the QR → tap Take Photo → auto-uploads. Every open /testphotowall tab updates live. No tournament setup required."
      accentColor="#EC4899">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 10 }}>
        <QRCodeCanvas value={uploadUrl} size={88} includeMargin />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>
            PHONE UPLOAD URL
          </div>
          <div style={{ fontSize: 11.5, marginTop: 2, wordBreak: 'break-all' }}>
            {uploadUrl.replace(/^https?:\/\//, '')}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
            Watch wall: {wallUrl.replace(/^https?:\/\//, '')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <PrimaryBtn href={wallUrl} color="#EC4899"><ExternalLink size={13} /> Open Photo Wall</PrimaryBtn>
        <PrimaryBtn href={uploadUrl} color="#A855F7"><ExternalLink size={13} /> Open Phone Capture</PrimaryBtn>
        <GhostBtn onClick={cleanup} disabled={busy || photoCount === 0} danger>
          <Trash2 size={13} /> Clean Up ({photoCount})
        </GhostBtn>
      </div>
      <StatusLine busy={busy} error={err} ok={ok} />
    </Tile>
  )
}

// ── Tile 5: Calcutta Test ────────────────────────────────────────────
function CalcuttaTile({ origin }) {
  const [active, setActive] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const seed = async () => {
    setBusy(true); setErr('')
    try {
      const result = await seedTestTournament('fourBallMatch', {
        tile: 'calcutta',
        name: 'TEST · Calcutta',
      })
      setActive(result)
    } catch (e) {
      setErr(e?.message || 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  const teardown = async () => {
    if (!active) return
    setBusy(true); setErr('')
    try {
      await deleteTestTournament(active.tournamentId)
      setActive(null)
    } catch (e) {
      setErr(e?.message || 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Tile
      Icon={DollarSign}
      title="Calcutta Test"
      blurb="Seeds a 4-Ball Match Play tournament with flights and teams, then jumps to the Calcutta auction + bid pages so you can verify bidding and payouts."
      accentColor="#34D399">
      {!active ? (
        <PrimaryBtn onClick={seed} disabled={busy} color="#34D399">Seed Auction</PrimaryBtn>
      ) : (
        <>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
            Tournament ID: <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 4 }}>{active.tournamentId}</code>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <PrimaryBtn href={`${origin}/calcutta?t=${active.tournamentId}`} color="#34D399"><ExternalLink size={13} /> Auction</PrimaryBtn>
            <PrimaryBtn href={`${origin}/calcutta-bid?t=${active.tournamentId}`} color="#34D399"><ExternalLink size={13} /> Bidder View</PrimaryBtn>
            <PrimaryBtn href={`${origin}/admin/t/${active.tournamentId}`}><ExternalLink size={13} /> Open in Admin</PrimaryBtn>
            <GhostBtn onClick={teardown} disabled={busy} danger><Trash2 size={13} /> Delete Test Data</GhostBtn>
          </div>
        </>
      )}
      <StatusLine busy={busy && !active} error={err} ok={active ? 'Auction ready — verify bidding + payouts.' : ''} />
    </Tile>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────

// Flatten a demo-format definition (single- or multi-flight) into a flat
// list of {flightId, teamId, t}. Mirrors the same logic DemoLauncher uses.
function collectTeams(def) {
  if (!def) return []
  const flightMap = def.flights
    ? def.flights()
    : { flightA: { teams: def.teams ? def.teams() : {} } }
  const out = []
  for (const [flightId, flight] of Object.entries(flightMap)) {
    for (const [teamId, t] of Object.entries(flight.teams || {})) {
      out.push({ flightId, teamId, t })
    }
  }
  return out
}
